import { styled } from 'styled-components'

import {
  FormattedMessage,
  Tag,
  TagFormat,
  TagType,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Spacing, Typography } from '@b-stock/fe-core/design-system'

import {
  sellerCollectsSearchResultContext,
  useSellerCollectsAnalytics,
} from '@/buyer-portal/helpers/sellerCollectsAnalytics'
import { getSearchResultSaleKind } from '@/buyer-portal/helpers/sellerCollectsSaleKind'
import { CreditCardTag } from '@/components/listings/CreditCardTag'
import { SellerCollectsTag } from '@/components/listings/SellerCollectsTag'
import { Standard } from '@/helpers/css/colors'
import {
  SellerCollectsEvent,
  SellerCollectsSurface,
} from '@/helpers/telemetry/SellerCollectsAnalyticsEvents'
import type { SearchResult } from '@/queries/listingSearchQuery'

import {
  LogoSkeleton,
  SkeletonTextLine,
  TagSkeleton,
} from '../../ListingCardSkeleton/ui'
import BuyingFormat, { BuyingFormatSkeleton } from './BuyingFormat'
import DurationAndFrequency from './DurationAndFrequency'
import FirstDibsOpensIn from './FirstDibsOpensIn'
import FirstDibsTag from './FirstDibsTag'
import InventoryTypeAndCondition from './InventoryTypeAndCondition'
import { Location } from './Location'
import PriceDetails, { PriceDetailsSkeleton } from './PriceDetails'
import StorefrontLogo, { LogoWrapper } from './StorefrontLogo'
import TimeLeftAndBidCount, {
  TimeLeftRowContainer,
} from './TimeLeftAndBidCount'
import Title, { TitleSkeleton } from './Title'
import UnitsAndMsrp, { UnitsAndMsrpSkeleton } from './UnitsAndMsrp'

const ListingInfoSection = styled.div`
  display: flex;
  ${Spacing.Primitive.S.css('padding')}
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  flex: 1 0 0;
  align-self: stretch;
`

const TopInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
`

const LogoRowAndDivider = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  align-self: stretch;
`

const Divider = styled.div`
  width: 100%;
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
`

const LogoRow = styled.div`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.very_dark_grey};

  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  align-self: stretch;
  flex-wrap: nowrap;
`

const BuyingFormatAndTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  width: 100%;
`

const BottomInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1 0 0;
  align-self: stretch;
`

const FormatTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
`

const NewSellerTag = styled(Tag)`
  white-space: nowrap;
  line-height: 1rem;
`

const FlatShipRateTag = styled(Tag)`
  background-color: ${Standard.Orange};
  color: ${Colors.Semantic.Neutral.white};
  white-space: nowrap;
  line-height: 1rem;
`

/**
 * Split out so the analytics hook, and the buyer-history lookup behind it,
 * mount only on a Seller Collects card rather than on every card in the grid.
 */
const SellerCollectsCardTag = ({ listing }: { listing: SearchResult }) => {
  // `sellerId` is the seller's account id — `useIsUserEligibleToBid` compares
  // it against the viewer's own to spot a seller looking at their own lot.
  const { trackSellerCollectsEvent } = useSellerCollectsAnalytics({
    sellerAccountId: listing.sellerId,
  })
  const saleKind = getSearchResultSaleKind(listing)

  return (
    <SellerCollectsTag
      sellerName={listing.storefrontName}
      saleKind={saleKind}
      onExplainerOpened={() =>
        trackSellerCollectsEvent(SellerCollectsEvent.ExplainerOpened, {
          ...sellerCollectsSearchResultContext(listing, saleKind),
          surface: SellerCollectsSurface.ListingGrid,
        })
      }
      data-testid="alp-seller-collects-tag"
    />
  )
}

const ListingInfo = ({
  listing,
  currentPrice,
  isFdBuyNow,
  endTime,
  hasTermsRestrictions,
  isNewSeller,
  showCreditCardTag,
  showSellerCollectsTag,
}: {
  listing: SearchResult
  currentPrice: number
  isFdBuyNow: boolean
  endTime: Date
  hasTermsRestrictions: boolean
  isNewSeller: boolean
  showCreditCardTag: boolean
  showSellerCollectsTag: boolean
}) => {
  return (
    <ListingInfoSection>
      <TopInfoSection>
        <LogoRowAndDivider>
          <LogoRow>
            <StorefrontLogo listing={listing} />
            <Location listing={listing} />
          </LogoRow>
          <Divider />
        </LogoRowAndDivider>
        <BuyingFormatAndTitle>
          <FormatTagsRow>
            <BuyingFormat listing={listing} isFdBuyNow={isFdBuyNow} />
            <FirstDibsTag listing={listing} />
            {showCreditCardTag && (
              <CreditCardTag data-testid="alp-credit-card-tag" />
            )}
            {showSellerCollectsTag && (
              <SellerCollectsCardTag listing={listing} />
            )}
            {isNewSeller && (
              <NewSellerTag type={TagType.info} data-testid="new-seller-tag">
                <FormattedMessage id="Auction.tag.newSeller" />
              </NewSellerTag>
            )}
            {listing.effectiveFlatRateCost != null && (
              <FlatShipRateTag
                format={TagFormat.type}
                type={TagType.normal}
                data-testid="flat-ship-rate-tag"
              >
                <FormattedMessage id="Auction.tag.flatShipRate" />
              </FlatShipRateTag>
            )}
          </FormatTagsRow>
          <Title listing={listing} />
        </BuyingFormatAndTitle>
        <InventoryTypeAndCondition listing={listing} />
        {listing.listingType === 'CONTRACT' ? (
          <DurationAndFrequency listing={listing} />
        ) : (
          <UnitsAndMsrp listing={listing} />
        )}
      </TopInfoSection>
      <BottomInfoSection>
        <PriceDetails
          listing={listing}
          currentPrice={currentPrice}
          hasTermsRestrictions={hasTermsRestrictions}
        />
        <TimeLeftAndBidCount listing={listing} endTime={endTime} />
        <FirstDibsOpensIn listing={listing} />
      </BottomInfoSection>
    </ListingInfoSection>
  )
}

export default ListingInfo

export const ListingInfoSkeleton = () => (
  <ListingInfoSection>
    <TopInfoSection>
      <LogoRowAndDivider>
        <LogoRow>
          <LogoWrapper>
            <LogoSkeleton data-testid="skeleton-storefront" />
          </LogoWrapper>
          <SkeletonTextLine width="100%" testIdSuffix="location" />
        </LogoRow>
        <Divider />
      </LogoRowAndDivider>
      <BuyingFormatAndTitle>
        <BuyingFormatSkeleton />
        <TitleSkeleton />
      </BuyingFormatAndTitle>
      <TagSkeleton format={TagFormat.combinedAttr} type={TagType.normal}>
        <SkeletonTextLine tag width="8rem" testIdSuffix="condition" />
      </TagSkeleton>
      <UnitsAndMsrpSkeleton />
    </TopInfoSection>
    <BottomInfoSection>
      <PriceDetailsSkeleton />
      <TimeLeftRowContainer>
        <SkeletonTextLine width="8rem" testIdSuffix="countdown" />
      </TimeLeftRowContainer>
    </BottomInfoSection>
  </ListingInfoSection>
)
